'use client';
import React, { useState } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';

const faqs = [
  {
    q: 'What is AgentOS?',
    a: 'AgentOS is your AI business partner for real estate. It helps you organize your business, prioritize opportunities, automate repetitive work, and start every day knowing exactly what matters most. It\'s not a CRM. It\'s the AI that runs alongside your business.',
  },
  {
    q: 'How is this different from a CRM?',
    a: "A CRM stores data. AgentOS thinks with you. It remembers every conversation, every client, every showing, and every promise — then tells you what to do next. It\'s the difference between a filing cabinet and a business partner.",
  },
  {
    q: 'How is this different from ChatGPT?',
    a: "ChatGPT starts from scratch every time. AgentOS builds persistent memory across your entire business. It knows your clients, your pipeline, your priorities, and your history. The longer you use it, the smarter it gets.",
  },
  {
    q: 'Do I need technical experience?',
    a: 'Not at all. If you can send a text message, you can use AgentOS. Just talk naturally — type or speak — and AgentOS handles the rest. Most agents are up and running in minutes.',
  },
  {
    q: 'What is the Morning Brief?',
    a: 'Every morning, AgentOS gives you a personalized briefing: who needs your attention today, what\'s due, where your best opportunities are, and what you promised clients. Start every day with clarity instead of chaos.',
  },
  {
    q: 'Is my data secure?',
    a: 'Yes. Your data belongs to you. We don\'t sell it, we don\'t train on your clients, and you can export everything anytime. No hidden automation. No surprises. Privacy first.',
  },
];

export default function FAQSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <section id="faq" className="py-24 section-alt-bg relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 50% 40% at 50% 50%, rgba(0,212,255,0.04) 0%, transparent 70%)' }}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <div className="section-badge mb-4">
            <Sparkles size={12} />
            Resources
          </div>
          <h2
            className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4"
            style={{ color: '#F0F4FF' }}
          >
            Common{' '}
            <span className="gradient-cyan-text">questions.</span>
          </h2>
          <p className="text-lg" style={{ color: 'rgba(240,244,255,0.55)' }}>
            Everything you need to know about AgentOS.
          </p>
        </div>

        <div className="space-y-2">
          {faqs?.map((faq, idx) => (
            <div
              key={idx}
              className="rounded-2xl overflow-hidden transition-all duration-200"
              style={{
                border: openIdx === idx ? '1px solid rgba(0,212,255,0.25)' : '1px solid rgba(255,255,255,0.08)',
                background: openIdx === idx ? 'rgba(0,212,255,0.05)' : 'rgba(255,255,255,0.04)',
              }}
            >
              <button
                className="w-full flex items-center justify-between px-6 py-5 text-left"
                onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
              >
                <span className="font-semibold text-base pr-4" style={{ color: '#F0F4FF' }}>{faq?.q}</span>
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200"
                  style={{
                    background: openIdx === idx ? 'linear-gradient(135deg, #00D4FF, #0099CC)' : 'rgba(255,255,255,0.08)',
                    transform: openIdx === idx ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  <ChevronDown
                    size={14}
                    style={{ color: openIdx === idx ? '#0B0F19' : 'rgba(240,244,255,0.40)' }}
                    strokeWidth={2.5}
                  />
                </div>
              </button>
              {openIdx === idx && (
                <div className="px-6 pb-5">
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(240,244,255,0.65)' }}>{faq?.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
